import { memo, useEffect, useRef, useState, useCallback, useContext, useMemo } from 'react';
import { VariableSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { useDispatch, useSelector } from 'react-redux';
import api from 'services/api';
import { AutoSizer } from 'react-virtualized';

import { addData } from 'store/secondaryVideoTabsDataSlice';
// import { updateTabContent, getScrollPosition, getItemSizes } from 'store/secondaryViewSlice';
import { RootState } from 'store';
import TranscriptItem from './_components/TranscriptItem';
import { getScrollPosition, updateTabContent, getItemSizes } from 'store/secondaryChannelVideoSlice';
import { ModalContext } from 'context/ModalContext';
import { MODAL_ANIMATIONS, MODAL_TYPES } from 'components/Modal/constants';

import { removeTranscript } from 'api/transcriptsAPI';
import { setTranscripts } from 'store/transcriptSlice';

interface ITranscriptView {
  secondaryViewData?: any;
  keywords: null | any;
  showHighlight: boolean;
  navigateVideoPage?: any;
  videoHashId: string;
  channelId: string;
  currentVideoShot: number;
  secondaryViewSlice: any;
  selected: number;
  videoShots: any;
  isResizing: boolean;
}

function TranscriptView2({
  secondaryViewData,
  navigateVideoPage,
  keywords,
  showHighlight,
  videoHashId,
  channelId,
  currentVideoShot,
  secondaryViewSlice,
  selected,
  videoShots,
  isResizing
}: ITranscriptView) {
  const [allTranscripts, setAllTranscripts] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [nextUrl, setNextUrl] = useState<string>('');
  const keyId = channelId + videoHashId + currentVideoShot;
  const dispatch = useDispatch();
  const userData = useSelector((state: RootState) => state.user.user);
  const itemSizeMap = useRef(new Map());
  const listRef = useRef<VariableSizeList | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const dimensionsRef = useRef({ width: 0, height: 0 });
  const scrollPosition = useRef<number>(0);
  const isProgramScroll = useRef<boolean>(false);
  const videoShotIdRef = useRef<string | null>(null);
  const [editingId, setEditingId] = useState<null | string>(null);
  const { showModal } = useContext(ModalContext);
  const [hoveredId, setHoveredId] = useState(null as any);

  const handleEdit = (id: string) => {
    if (editingId !== null && editingId !== id) {
      showModal(MODAL_TYPES.edit_summary, {
        animation: MODAL_ANIMATIONS.fade,
        channelId,
        setEditingId,
        editingId: id,
        itemType: 'transcript'
      });
    } else {
      setEditingId(id);
    }
  };

  const videoShotId = useMemo(() => {
    if (!videoShots?.length) return;
    const idx = videoShots[currentVideoShot]?.shot_hash_id;
    return idx;
  }, [videoHashId, channelId, videoShots, currentVideoShot]);

  const defaultScrollTop = getScrollPosition(secondaryViewSlice, channelId, videoHashId, 'transcripts');
  const defaultItemSizes = getItemSizes(secondaryViewSlice, channelId, videoHashId, 'transcripts');

  useEffect(() => {
    videoShotIdRef.current = videoShotId;
  }, [videoShotId]);

  const transcriptData = useSelector(
    (state: RootState) => state.secondaryViewTab.videos.find((video) => video.videoShotId === videoShotId)?.transcripts
  );

  useEffect(() => {
    if (transcriptData) {
      console.log('set trascript data');
      setAllTranscripts(transcriptData?.results);
    }
  }, [transcriptData]);

  const fetchData = (url: string) => {
    setLoading(true);
    api.get(url).then((res) => {
      const { results } = res.data;
      const { next } = res.data.links;
      setAllTranscripts((prevData: any[]) => {
        const updatedData = [...prevData, ...results];
        dispatch(
          addData({
            videoHashId: keyId,
            videoShotId: videoShotId,
            key: 'transcripts',
            data: {
              results: updatedData,
              nextUrl: next
            }
          })
        );
        return updatedData;
      });
      setNextUrl(next);
      setLoading(false);
    });
  };

  useEffect(() => {
    let mounted = true;
    if (!videoShotIdRef.current) return;
    const defaultUrl =
      secondaryViewData.channelId === 'home'
        ? `/api/videos/${videoHashId}/shots/${videoShotIdRef.current}/transcripts/?page_size=45`
        : `/api/videos/${videoHashId}/shots/${videoShotIdRef.current}/${channelId}/transcripts/?page_size=45`;

    if (transcriptData) {
      const { results, nextUrl } = transcriptData;
      setAllTranscripts(results);
      setNextUrl(nextUrl);
      console.log('set scroll positions', defaultScrollTop);
      if (defaultItemSizes) {
        itemSizeMap.current = new Map(Object.entries(defaultItemSizes).map(([key, value]) => [Number(key), value]));
      }
      if (defaultScrollTop !== undefined) {
        isProgramScroll.current = true;
        scrollPosition.current = defaultScrollTop;
        listRef?.current?.scrollTo(defaultScrollTop);
      }
    } else {
      if (mounted) {
        fetchData(defaultUrl);
      }
    }

    return () => {
      mounted = false;
      if (scrollPosition.current !== undefined && itemSizeMap.current) {
        const itemSizeObj = Object.fromEntries(itemSizeMap.current);
        dispatchUpdatedContent(scrollPosition.current, itemSizeObj);
        setAllTranscripts([]);
        setNextUrl('');
        videoShotIdRef.current = null;
        scrollPosition.current = 0;
        listRef.current?.resetAfterIndex(0, true);
      }
    };
  }, [channelId, videoHashId, videoShotId, selected]);

  const dispatchUpdatedContent = (currentPosition: number, itemSizes: any) => {
    if (currentPosition !== undefined) {
      dispatch(
        updateTabContent({
          channelHashId: channelId,
          videoHashId: videoHashId,
          data: {
            transcripts: {
              scrollPosition: currentPosition,
              itemSizes: itemSizes
            }
          }
        })
      );
    }
  };

  const loadMoreItems = (startIndex: number, stopIndex: number) => {
    if (!loading && nextUrl) {
      fetchData(nextUrl);
    }
  };

  const isItemLoaded = (index: number) => index < allTranscripts.length;

  const getItemSize = (index: number) => {
    return itemSizeMap.current.get(index) || 50; // default size of 50, item will be resized dynamically
  };

  const setItemSize = (index: number, size: number) => {
    const currentSize = itemSizeMap.current.get(index);
    if (currentSize !== size && listRef.current) {
      // Ensure listRef.current is not null
      itemSizeMap.current.set(index, size + 4); // add 4px for height to make room
      // Optionally, you can force the list to recompute its layout without triggering a re-render
      // This prevents the scrolling behavior from resetting to the top
      listRef.current.resetAfterIndex(index, true);
    }
  };

  const ItemRenderer = ({ index, style }: { index: number; style: React.CSSProperties }) => {
    const transcript = allTranscripts[index];
    const displayName = transcript?.user_info?.display_name
      ? transcript?.user_info?.display_name
      : transcript?.user_info?.name;

    let displayAvatar = true;

    const isLastItem = index === allTranscripts.length - 1;
    const itemStyle = {
      ...style
    };

    if (index > 0 && transcript?.user_info) {
      const previousTranscript = allTranscripts[index - 1];
      if (previousTranscript && previousTranscript.user_info) {
        displayAvatar = previousTranscript.user_info.hash_id !== transcript.user_info.hash_id;
      }
    }

    const itemRef = useCallback(
      (node) => {
        if (node !== null) {
          const height = node.getBoundingClientRect().height;
          const currentSize = itemSizeMap.current.get(index);

          // Only update if the size has changed to prevent unnecessary renders
          if (!currentSize || currentSize !== height) {
            setItemSize(index, height);
          }
        }
      },
      [index]
    );

    if (!transcript) {
      return (
        <div style={{ ...style, height: '40px' }} className="w-full bg-white flex justify-center">
          Loading...
        </div>
      );
    }

    const handleDelete = (transcriptHashId: string) => {
      const confirmDelete = async () => {
        await removeTranscript(videoHashId, videoShotId, transcriptHashId);
      };

      showModal(MODAL_TYPES.remove_summary, {
        animation: MODAL_ANIMATIONS.fade,
        modalMessage: {
          title: 'Deleting Transcript',
          text: 'You are about to to delete a transcript.  Click "Confirm to delete" button.'
        },
        onConfirm: confirmDelete
      });
    };

    return (
      <div
        style={itemStyle}
        className={`relative w-full ${!isResizing ? 'hover:bg-[#f8f8f8]' : ''}`}
        id={transcript.hash_id}
      >
        <div ref={itemRef} className="py-1">
          <TranscriptItem
            key={transcript.hash_id}
            videoHashId={videoHashId}
            shotId={videoShotId}
            hashId={transcript.hash_id}
            displayAvatar={displayAvatar}
            user={{ avatar: transcript.user_info.avatar, display_name: displayName }}
            content={transcript.content}
            navigateLink={navigateVideoPage}
            time={transcript.start_time}
            videoShotIdx={currentVideoShot}
            userData={userData}
            handleEdit={() => {
              handleEdit(transcript.hash_id);
            }}
            isEditing={editingId === transcript.hash_id}
            handleCancelEdit={() => {
              setEditingId(null);
            }}
            onDelete={() => {
              handleDelete(transcript.hash_id);
            }}
            hoveredItemId={hoveredId}
            handleHoveredId={() => {
              setHoveredId(transcript.hash_id);
            }}
          />
        </div>
        {isLastItem && <div className="h-5 w-full"></div>}
      </div>
    );
  };

  const handleScrollY = ({ scrollOffset, scrollUpdateWasRequested }: any) => {
    if (scrollOffset !== undefined && !scrollUpdateWasRequested) {
      if (!isProgramScroll.current) {
        scrollPosition.current = scrollOffset;
      }

      setTimeout(() => {
        isProgramScroll.current = false;
      }, 0);
    }
  };

  useEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(1, true); // Reset all items
    }
  }, [dimensions]);

  return (
    <div
      className={`absolute w-full flex flex-col flex-1 top-0 right-0 left-0 bottom-0 overflow-y-auto pb-2`}
      style={{ height: '100%', width: '100%' }}
    >
      <AutoSizer>
        {({ height, width }: any) => {
          if (dimensionsRef.current.width !== width || dimensionsRef.current.height !== height) {
            dimensionsRef.current = { width, height };
            // Schedule the state update outside the render phase
            requestAnimationFrame(() => setDimensions({ width, height }));
          }

          return (
            <div className="relative" ref={contentRef}>
              <InfiniteLoader
                key={videoShotId}
                isItemLoaded={isItemLoaded}
                itemCount={allTranscripts.length + (nextUrl ? 1 : 0)}
                loadMoreItems={loadMoreItems}
              >
                {({ onItemsRendered, ref }) => (
                  <VariableSizeList
                    height={height}
                    key={videoShotId}
                    itemCount={allTranscripts.length + (nextUrl ? 1 : 0)}
                    itemSize={getItemSize}
                    onItemsRendered={onItemsRendered}
                    ref={(list) => {
                      // Assigning the ref to the provided ref and calling resetAfterIndex when necessary
                      ref(list);
                      if (list) {
                        listRef.current = list;
                      }
                    }}
                    width={width}
                    onScroll={(scrollInfo) => {
                      handleScrollY(scrollInfo); // Call the scroll handler
                    }}
                  >
                    {ItemRenderer}
                  </VariableSizeList>
                )}
              </InfiniteLoader>
            </div>
          );
        }}
      </AutoSizer>
    </div>
  );
}

export default memo(TranscriptView2);
